import React from 'react';
import {
  ChevronDown,
  ChevronRight,
  History,
  MapPin,
  Route as RouteIcon,
  Star,
  User as UserIcon
} from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import ChangeLogTable from './ChangeLogTable';
import { cn } from '../../lib/cn';
import { actionBadge, formatDateTime } from '../../utils/commandCenterHelpers';

export default function EntityActivityCard({
  group,
  expanded,
  onToggle,
  openVersions,
  onToggleVersion,
  onReactivate
}) {
  const { entity } = group;
  const EntityIcon = entity.entityType === 'route' ? RouteIcon : MapPin;

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
        )}
        <span className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 inline-flex items-center justify-center shrink-0">
          <EntityIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-[14px] font-semibold text-slate-900 truncate">{entity.name}</p>
            <Badge variant="outline" size="sm">
              {entity.label}
            </Badge>
            {!entity.exists && (
              <Badge variant="danger" size="sm">
                Deleted
              </Badge>
            )}
          </div>
          <p className="text-[12px] text-slate-500 font-mono truncate">{entity.entityRef}</p>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-right shrink-0">
          <MiniMeta label="Saves" value={group.saveCount} />
          <MiniMeta label="Current" value={`V${group.currentVersionNumber ?? '—'}`} />
          <MiniMeta label="Revisions" value={group.totalVersions} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/40 px-4 py-3">
          <div className="flex items-center justify-between mb-2 text-[11.5px] text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Revision timeline
            </span>
            <span>
              First {formatDateTime(group.firstSavedAt)} · Last {formatDateTime(group.lastSavedAt)}
            </span>
          </div>

          <ol className="relative ml-1.5 border-l border-slate-200 space-y-2.5 pl-4">
            {group.versions.map((version) => (
              <VersionRow
                key={version.id}
                entity={entity}
                version={version}
                open={openVersions.has(version.id)}
                onToggle={() => onToggleVersion(version.id)}
                onReactivate={() => onReactivate(entity, group.versions, version)}
              />
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function VersionRow({ entity, version, open, onToggle, onReactivate }) {
  const badge = actionBadge(version.action);

  return (
    <li className="relative">
      <span
        className={cn(
          'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white',
          version.isCurrent ? 'bg-emerald-500' : 'bg-slate-300'
        )}
      />
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-2 min-w-0 flex-1 text-left"
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            )}
            <span className="text-[13px] font-semibold text-slate-900 tabular-nums shrink-0">
              V{version.versionNumber}
            </span>
            {version.isCurrent && (
              <Badge variant="success" size="sm">
                <Star className="h-2.5 w-2.5" /> Current
              </Badge>
            )}
            <Badge variant={badge.variant} size="sm">
              {badge.label}
            </Badge>
            <span className="text-[12px] text-slate-500 truncate">{version.summary}</span>
          </button>

          <div className="flex items-center gap-2 shrink-0">
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[11.5px]',
                version.savedByThisUser ? 'text-brand-700 font-medium' : 'text-slate-400'
              )}
            >
              <UserIcon className="h-3 w-3" />
              {version.savedByThisUser ? 'By this user' : version.savedByLabel}
            </span>
            {!version.isCurrent && (
              <Button size="xs" variant="outline" onClick={onReactivate} leftIcon={History}>
                Reactivate
              </Button>
            )}
          </div>
        </div>

        <div className="px-3 pb-2 flex items-center gap-3 text-[11px] text-slate-400 border-t border-slate-50 pt-1.5">
          <span>{formatDateTime(version.savedAt)}</span>
          <span>·</span>
          <span>{version.changeCount} change row{version.changeCount === 1 ? '' : 's'}</span>
          {version.lastReactivatedAt && (
            <>
              <span>·</span>
              <span className="text-amber-600">
                Reactivated {formatDateTime(version.lastReactivatedAt)}
              </span>
            </>
          )}
        </div>

        {open && (
          <div className="px-3 pb-3">
            <ChangeLogTable changeLogs={version.changeLogs} />
          </div>
        )}
      </div>
    </li>
  );
}

function MiniMeta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-[13px] font-semibold text-slate-900 tabular-nums">{value}</p>
    </div>
  );
}
